<template>
  <div class="earworms-container">
    <Header :title="'Earworms'" :user="currentUser" />
    
    <main class="page-main">
      <div class="action-btn-bar">
        <button @click="createNewEarworm" class="action-btn construct-btn">New Earworm</button>
      </div>
      
      <div v-if="loading" class="loading">Loading earworms...</div>
      <div v-else-if="earworms.length === 0" class="empty-state">
        <p>No earworms found</p>
      </div>
      <div v-else class="tiles-container">
        <div
          v-for="earworm in earworms"
          :key="earworm.id"
          class="tile earworm-tile"
          @click="editEarworm(earworm.id)"
          style="cursor:pointer;"
        >
          <div class="earworm-header">
            <div class="earworm-image">
              <img v-if="earworm.image_url" :src="earworm.image_url" alt="Earworm Image" />
              <div v-else class="placeholder-image">
                <span>🎵</span>
              </div>
            </div>
            <div class="earworm-info">
              <h3>{{ earworm.name }}</h3>
              <div v-if="earworm.userPlays !== undefined || earworm.userVictories !== undefined" class="earworm-stats">
                <small v-if="earworm.userPlays !== undefined" class="stat-item">
                  🎵 {{ earworm.userPlays }} plays
                </small>
                <small v-if="earworm.userVictories !== undefined" class="stat-item">
                  🏆 {{ earworm.userVictories }} victories
                </small>
              </div>
            </div>
          </div>
          
          <div class="earworm-actions">
            <button 
              @click.stop="editEarworm(earworm.id)" 
              class="action-btn edit-btn"
              title="Edit earworm"
            >
              ✏️ Edit
            </button>
            
            <!-- Play Button - opens earworm in player -->
            <button 
              v-if="canPlayEarworm(earworm)"
              @click.stop="playEarworm(earworm.id)" 
              class="action-btn play-btn"
              title="Open earworm in player"
            >
              ▶️ Play
            </button>
            
            <!-- Download Button - shows only when server audio is available -->
            <button 
              v-if="earworm.audio_url"
              @click.stop="downloadEarworm(earworm)" 
              class="action-btn download-btn"
              :disabled="isDownloading(earworm.id)"
              title="Download for offline playback"
            >
              {{ isDownloading(earworm.id) ? '⬇️ Downloading...' : '⬇️ Download' }}
            </button>
            
            <!-- Show generating status -->
            <span 
              v-else-if="isGenerating(earworm)"
              class="music-status generating"
              title="Music is being generated"
            >
              🎵 {{ getGenerationLabel(earworm) }}
            </span>
            
            <!-- Show no audio status -->
            <span 
              v-else
              class="music-status no-audio"
              title="No audio available"
            >
              🎶 No audio
            </span>
            
            <button 
              @click.stop="deleteEarworm(earworm.id)" 
              class="action-btn delete-btn"
              title="Delete earworm"
            >
              🗑️ Delete
            </button>
          </div>
          
          <div class="earworm-meta">
            <small>Created: {{ formatDate(typeof earworm.created_at === 'string' ? earworm.created_at : (earworm.created_at ? earworm.created_at.toISOString() : '')) }}</small>
            <small v-if="offlineAudioAvailable.has(earworm.id) && earworm.downloaded_at">
              <br>Downloaded: {{ formatDate(typeof earworm.downloaded_at === 'string' ? earworm.downloaded_at : (earworm.downloaded_at ? earworm.downloaded_at.toISOString() : '')) }}
            </small>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import Header from '../components/Header.vue'
import type { Earworm } from '../services/database'

// Interface for earworm data stored in IndexedDB
interface StoredEarworm {
  id: string
  user_id: string | null
  name: string
  image_url?: string | null
  data?: string | null
  analysis?: string | null
  transcript?: string | null
  audio_url?: string | null
  status?: string | null
  created_at?: string | Date | null
  modified_at?: string | Date | null
  downloaded_at?: string | Date | null
  audioBlob: Blob | null
  userPlays?: number
  userVictories?: number
}

// Interface for user data stored in IndexedDB
interface StoredUser {
  id: string
  username?: string
  email: string
  type?: string
  family_id?: string
  profileImageDataUrl?: string
  data?: any
  activityStats?: any
  stored_at: Date
  needs_sync: boolean
}

// Interface for sync queue items
interface SyncItem {
  id?: number
  type: 'user_update' | 'activity_record'
  user_id: string
  data: any
  created_at: Date
  retry_count: number
}

const router = useRouter()
const earworms = ref<Earworm[]>([])
const loading = ref(false)
const currentUser = ref<any>(null)

// Audio playback state - removed since we don't play in tiles anymore
// const currentAudio = ref<HTMLAudioElement | null>(null)
// const currentlyPlayingId = ref<Earworm['id'] | null>(null)
// const currentTime = ref(0)
// const currentWordIndex = ref(-1)
// const animationFrame = ref<number | null>(null)

// Download state
const downloadingIds = ref<Set<Earworm['id']>>(new Set())

// Offline audio availability tracking
const offlineAudioAvailable = ref<Set<Earworm['id']>>(new Set())

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'

const getPrimaryAudioUrl = (earworm: Earworm) => earworm.audio_url || ''
const hasAudio = (earworm: Earworm) => Boolean(getPrimaryAudioUrl(earworm))
const canPlayEarworm = (earworm: Earworm) => {
  // Can play if we have offline audio OR if we're online and have server audio
  const hasOffline = offlineAudioAvailable.value.has(earworm.id)
  const isOnline = navigator.onLine
  return hasOffline || (isOnline && hasAudio(earworm))
}
const getTimestampedLyrics = (earworm: Earworm) => earworm.transcript ?? null

const GENERATION_STATES = ['pending', 'processing', 'generating', 'queued', 'building']

const getGenerationLabel = (earworm: Earworm) => {
  const statusText =
    (typeof earworm.status === 'string' ? earworm.status : '') ||
    'processing...'
  return statusText
}

const isGenerating = (earworm: Earworm) => {
  if (hasAudio(earworm)) return false
  const status = getGenerationLabel(earworm).toLowerCase()
  return GENERATION_STATES.some((state) => status.includes(state))
}

onMounted(async () => {
  try {
    const userStr = sessionStorage.getItem('currentUser')
    if (userStr) {
      currentUser.value = JSON.parse(userStr)
    }
  } catch (error) {
    console.error('Earworms: Error parsing user data from sessionStorage:', error)
  }

  // Set up online/offline sync
  const handleOnline = () => {
    console.log('🌐 Back online - syncing user data')
    syncUserData()
  }
  
  window.addEventListener('online', handleOnline)
  
  // Cleanup
  onUnmounted(() => {
    window.removeEventListener('online', handleOnline)
  })

  await loadEarworms()
})

const loadEarworms = async () => {
  loading.value = true
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (sessionToken) {
      headers.Authorization = `Bearer ${sessionToken}`
    }

    const response = await fetch(`${API_BASE_URL}/api/earworms`, { headers })
    if (!response.ok) {
      throw new Error(`Failed to load earworms: ${response.status} ${response.statusText}`)
    }
    const serverEarworms = (await response.json()) as Earworm[]
    
    // Store user data offline for future use
    if (userData) {
      await storeUserOffline(userData, false) // Store without sync flag since we just got fresh data
      currentUser.value = userData
    }
    
    // Load offline earworms and merge with server data
    const offlineEarworms = await loadOfflineEarworms()
    const mergedEarworms = mergeEarworms(serverEarworms, offlineEarworms)
    
    // Update offline audio availability tracking
    offlineAudioAvailable.value.clear()
    for (const earworm of mergedEarworms) {
      const storedEarworm = await getStoredEarworm(earworm.id)
      if (storedEarworm?.audioBlob) {
        offlineAudioAvailable.value.add(earworm.id)
      }
    }
    
    // Load activity stats for each earworm
    await loadEarwormActivityStats(mergedEarworms)
    
    earworms.value = mergedEarworms
  } catch (error) {
    console.error('Failed to load earworms from server, trying offline:', error)
    
    // Fallback to offline earworms and user data
    try {
      const userStr = sessionStorage.getItem('currentUser')
      const userData = userStr ? JSON.parse(userStr) : null
      
      const offlineEarworms = await loadOfflineEarworms()
      
      // Try to load user data from offline storage
      if (userData?.id) {
        const offlineUser = await loadOfflineUser(userData.id)
        if (offlineUser) {
          currentUser.value = offlineUser
          console.log('Loaded user data from offline storage')
        }
      }
      
      // Update offline audio availability tracking
      offlineAudioAvailable.value.clear()
      for (const earworm of offlineEarworms) {
        const storedEarworm = await getStoredEarworm(earworm.id)
        if (storedEarworm?.audioBlob) {
          offlineAudioAvailable.value.add(earworm.id)
        }
      }
      
      // Load activity stats for offline earworms (may be limited when offline)
      await loadEarwormActivityStats(offlineEarworms)
      
      earworms.value = offlineEarworms
      console.log('Loaded earworms from offline storage')
    } catch (offlineError) {
      console.error('Failed to load offline earworms:', offlineError)
      earworms.value = []
    }
  } finally {
    loading.value = false
  }
}

// Load per-earworm activity stats for the current user
const loadEarwormActivityStats = async (earworms: Earworm[]): Promise<void> => {
  if (!currentUser.value?.id) return

  try {
    // Get user activity data which should include per-earworm stats
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (sessionToken) {
      headers.Authorization = `Bearer ${sessionToken}`
    }

    // Fetch user data which includes activity information
    const response = await fetch(`${API_BASE_URL}/api/users/${currentUser.value.id}`, { headers })

    if (response.ok) {
      const userData = await response.json()

      // Extract per-earworm activity stats from user data
      // The server should provide activity breakdown by earworm
      if (userData.data?.activity?.earwormActivity) {
        const earwormStats = userData.data.activity.earwormActivity

        // Update earworms with their activity stats
        earworms.forEach(earworm => {
          const stats = earwormStats[earworm.id]
          if (stats) {
            earworm.userPlays = stats.plays || 0
            earworm.userVictories = stats.victories || 0
          } else {
            earworm.userPlays = 0
            earworm.userVictories = 0
          }
        })
      } else {
        // Fallback: set all stats to 0 if no per-earworm data available
        console.log('Per-earworm activity stats not available in user data')
        earworms.forEach(earworm => {
          earworm.userPlays = 0
          earworm.userVictories = 0
        })
      }

      // Update current user activity stats
      if (userData.data?.activity) {
        currentUser.value.activityStats = userData.data.activity
      }
    } else {
      console.error('Failed to load user activity data for earworm stats')
      // Set defaults
      earworms.forEach(earworm => {
        earworm.userPlays = 0
        earworm.userVictories = 0
      })
    }
  } catch (error) {
    console.error('Failed to load earworm activity stats:', error)
    // Set defaults
    earworms.forEach(earworm => {
      earworm.userPlays = 0
      earworm.userVictories = 0
    })
  }
}

const createNewEarworm = async () => {
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    if (sessionToken) {
      headers.Authorization = `Bearer ${sessionToken}`
    }

    const payload: Partial<Earworm> = {
      name: 'Untitled Earworm',
      user_id: userData?.id || null,
      analysis: null,
      transcript: null,
      audio_url: null,
      status: null
    }

    const response = await fetch(`${API_BASE_URL}/api/earworms`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      throw new Error('Failed to create new earworm')
    }

    const result = await response.json()
    router.push({ name: 'earworm-builder', params: { id: result.id } })
  } catch (error) {
    alert('Failed to create new earworm: ' + (error instanceof Error ? error.message : 'Unknown error'))
  }
}

const editEarworm = (id: Earworm['id']) => {
  const earwormId = String(id)
  if (!earwormId || earwormId === 'undefined' || earwormId === 'null') {
    console.error('Invalid earworm ID for editing:', earwormId)
    return
  }
  router.push({ name: 'earworm-builder', params: { id: earwormId } })
}

const playEarworm = (id: Earworm['id']) => {
  const earwormId = String(id)
  if (!earwormId || earwormId === 'undefined' || earwormId === 'null') {
    console.error('Invalid earworm ID for playing:', earwormId)
    return
  }

  router
    .push({ name: 'earworm-player', params: { id: earwormId } })
    .catch((error) => {
      console.error('Navigation to earworm-player failed:', error)
    })
}

const deleteEarworm = async (id: Earworm['id']) => {
  if (!confirm('Are you sure you want to delete this earworm?')) {
    return
  }

  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (sessionToken) {
      headers.Authorization = `Bearer ${sessionToken}`
    }

    const response = await fetch(`${API_BASE_URL}/api/earworms/${id}`, {
      method: 'DELETE',
      headers
    })

    if (!response.ok) {
      throw new Error('Failed to delete earworm')
    }

    await loadEarworms()
  } catch (error) {
    console.error('Failed to delete earworm:', error)
    alert('Failed to delete earworm')
  }
}

// IndexedDB functions for offline storage
const openIndexedDB = () => {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open('HopToItEarworms', 2) // Increment version for schema update
    
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result
      
      // Create object store for earworm data
      if (!db.objectStoreNames.contains('earworms')) {
        const store = db.createObjectStore('earworms', { keyPath: 'id' })
        store.createIndex('user_id', 'user_id', { unique: false })
      }
      
      // Create object store for user data
      if (!db.objectStoreNames.contains('users')) {
        const store = db.createObjectStore('users', { keyPath: 'id' })
        store.createIndex('email', 'email', { unique: true })
      }
      
      // Create object store for sync queue
      if (!db.objectStoreNames.contains('syncQueue')) {
        const store = db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true })
        store.createIndex('type', 'type', { unique: false })
        store.createIndex('user_id', 'user_id', { unique: false })
      }
    }
  })
}

const storeEarwormOffline = async (earworm: Earworm) => {
  try {
    const db = await openIndexedDB()
    const transaction = db.transaction(['earworms'], 'readwrite')
    const store = transaction.objectStore('earworms')
    
    // Store the complete earworm data - create clean serializable object
    const earwormData = {
      id: earworm.id,
      user_id: earworm.user_id,
      name: earworm.name,
      image_url: earworm.image_url,
      data: earworm.data ? JSON.stringify(earworm.data) : null,
      analysis: earworm.analysis ? JSON.stringify(earworm.analysis) : null,
      transcript: earworm.transcript ? JSON.stringify(earworm.transcript) : null,
      audio_url: earworm.audio_url,
      status: earworm.status ? JSON.stringify(earworm.status) : null,
      created_at: earworm.created_at,
      modified_at: earworm.modified_at,
      downloaded_at: new Date(),
      userPlays: earworm.userPlays,
      userVictories: earworm.userVictories,
      // Store audio as blob if available
      audioBlob: null // We'll fetch and store this separately
    }
    
    await store.put(earwormData)
    
    db.close()
  } catch (error) {
    console.error('Failed to store earworm offline:', error)
  }
}

const getStoredEarworm = async (earwormId: Earworm['id']): Promise<StoredEarworm | null> => {
  try {
    const db = await openIndexedDB()
    const transaction = db.transaction(['earworms'], 'readonly')
    const store = transaction.objectStore('earworms')
    
    return new Promise<StoredEarworm | null>((resolve) => {
      const request = store.get(earwormId)
      request.onsuccess = () => resolve(request.result || null)
      request.onerror = () => resolve(null)
    })
  } catch (error) {
    console.error('Failed to get stored earworm:', error)
    return null
  }
}

const downloadEarworm = async (earworm: Earworm) => {
  if (!earworm.audio_url) {
    alert('No audio available to download')
    return
  }
  
  downloadingIds.value.add(earworm.id)
  
  try {
    // Fetch audio file
    const audioResponse = await fetch(earworm.audio_url)
    if (!audioResponse.ok) {
      throw new Error('Failed to download audio')
    }
    
    const audioBlob = await audioResponse.blob()
    
    // Store in IndexedDB - create a clean serializable object
    const db = await openIndexedDB()
    const transaction = db.transaction(['earworms'], 'readwrite')
    const store = transaction.objectStore('earworms')
    
    const earwormData = {
      id: earworm.id,
      user_id: earworm.user_id,
      name: earworm.name,
      image_url: earworm.image_url,
      data: earworm.data ? JSON.stringify(earworm.data) : null,
      analysis: earworm.analysis ? JSON.stringify(earworm.analysis) : null,
      transcript: earworm.transcript ? JSON.stringify(earworm.transcript) : null,
      audio_url: earworm.audio_url,
      status: earworm.status ? JSON.stringify(earworm.status) : null,
      created_at: earworm.created_at,
      modified_at: earworm.modified_at,
      downloaded_at: new Date(),
      userPlays: earworm.userPlays,
      userVictories: earworm.userVictories,
      audioBlob: audioBlob
    }
    
    await store.put(earwormData)
    
    db.close()
    
    // Mark as available offline
    offlineAudioAvailable.value.add(earworm.id)
    
    alert('Earworm downloaded for offline playback!')
  } catch (error) {
    console.error('Failed to download earworm:', error)
    alert('Failed to download earworm for offline use')
  } finally {
    downloadingIds.value.delete(earworm.id)
  }
}

const isDownloading = (earwormId: Earworm['id']) => {
  return downloadingIds.value.has(earwormId)
}

// Load offline earworms from IndexedDB
const loadOfflineEarworms = async (): Promise<Earworm[]> => {
  try {
    const db = await openIndexedDB()
    const transaction = db.transaction(['earworms'], 'readonly')
    const store = transaction.objectStore('earworms')
    
    return new Promise<Earworm[]>((resolve) => {
      const request = store.getAll()
      request.onsuccess = () => {
        const storedEarworms: StoredEarworm[] = request.result || []
        
        // Convert stored earworms back to Earworm format
        const earworms: Earworm[] = storedEarworms.map(stored => ({
          id: stored.id,
          user_id: stored.user_id,
          name: stored.name,
          image_url: stored.image_url || undefined,
          data: stored.data ? JSON.parse(stored.data) : undefined,
          analysis: stored.analysis ? JSON.parse(stored.analysis) : undefined,
          transcript: stored.transcript ? JSON.parse(stored.transcript) : undefined,
          audio_url: stored.audio_url || undefined,
          status: stored.status ? JSON.parse(stored.status) : undefined,
          created_at: stored.created_at,
          modified_at: stored.modified_at,
          downloaded_at: stored.downloaded_at,
          userPlays: stored.userPlays,
          userVictories: stored.userVictories
        }))
        
        resolve(earworms)
      }
      request.onerror = () => resolve([])
    })
  } catch (error) {
    console.error('Failed to load offline earworms:', error)
    return []
  }
}

// Merge server earworms with offline earworms, preferring server data
const mergeEarworms = (serverEarworms: Earworm[], offlineEarworms: Earworm[]): Earworm[] => {
  const merged = new Map<string, Earworm>()
  
  // Add all server earworms first
  serverEarworms.forEach(earworm => {
    merged.set(earworm.id, earworm)
  })
  
  // Merge offline earworms, preserving downloaded_at and other offline-specific data
  offlineEarworms.forEach(offlineEarworm => {
    const existing = merged.get(offlineEarworm.id)
    if (existing) {
      // Merge offline data with server data
      merged.set(offlineEarworm.id, {
        ...existing,
        downloaded_at: offlineEarworm.downloaded_at
      })
    } else {
      // Add offline-only earworm
      merged.set(offlineEarworm.id, offlineEarworm)
    }
  })
  
  return Array.from(merged.values())
}

// User offline storage functions
const storeUserOffline = async (user: any, needsSync = false) => {
  try {
    const db = await openIndexedDB()
    const transaction = db.transaction(['users'], 'readwrite')
    const store = transaction.objectStore('users')
    
    const storedUser: StoredUser = {
      id: user.id,
      username: user.username,
      email: user.email,
      type: user.type,
      family_id: user.family_id,
      profileImageDataUrl: user.profileImageDataUrl,
      data: user.data,
      activityStats: user.activityStats,
      stored_at: new Date(),
      needs_sync: needsSync
    }
    
    await store.put(storedUser)
    db.close()
    console.log('✅ User stored offline:', user.email)
  } catch (error) {
    console.error('❌ Failed to store user offline:', error)
  }
}

const getStoredUser = async (userId: string): Promise<StoredUser | null> => {
  try {
    const db = await openIndexedDB()
    const transaction = db.transaction(['users'], 'readonly')
    const store = transaction.objectStore('users')
    
    return new Promise<StoredUser | null>((resolve) => {
      const request = store.get(userId)
      request.onsuccess = () => {
        const stored = request.result
        if (stored) {
          resolve(stored)
        } else {
          resolve(null)
        }
      }
      request.onerror = () => resolve(null)
    })
  } catch (error) {
    console.error('Failed to get stored user:', error)
    return null
  }
}

const loadOfflineUser = async (userId: string): Promise<any | null> => {
  try {
    const storedUser = await getStoredUser(userId)
    if (storedUser) {
      // Convert back to user format
      const user = {
        id: storedUser.id,
        username: storedUser.username,
        email: storedUser.email,
        type: storedUser.type,
        family_id: storedUser.family_id,
        profileImageDataUrl: storedUser.profileImageDataUrl,
        data: storedUser.data,
        activityStats: storedUser.activityStats
      }
      console.log('📱 Loaded user from offline storage:', user.email)
      return user
    }
    return null
  } catch (error) {
    console.error('Failed to load offline user:', error)
    return null
  }
}

// Sync queue functions
const addToSyncQueue = async (type: SyncItem['type'], userId: string, data: any) => {
  try {
    const db = await openIndexedDB()
    const transaction = db.transaction(['syncQueue'], 'readwrite')
    const store = transaction.objectStore('syncQueue')
    
    const syncItem: SyncItem = {
      type,
      user_id: userId,
      data,
      created_at: new Date(),
      retry_count: 0
    }
    
    await store.add(syncItem)
    db.close()
    console.log('📋 Added to sync queue:', type, userId)
  } catch (error) {
    console.error('❌ Failed to add to sync queue:', error)
  }
}

const getSyncQueue = async (): Promise<SyncItem[]> => {
  try {
    const db = await openIndexedDB()
    const transaction = db.transaction(['syncQueue'], 'readonly')
    const store = transaction.objectStore('syncQueue')
    
    return new Promise<SyncItem[]>((resolve) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => resolve([])
    })
  } catch (error) {
    console.error('Failed to get sync queue:', error)
    return []
  }
}

const removeFromSyncQueue = async (syncId: number) => {
  try {
    const db = await openIndexedDB()
    const transaction = db.transaction(['syncQueue'], 'readwrite')
    const store = transaction.objectStore('syncQueue')
    
    await store.delete(syncId)
    db.close()
    console.log('🗑️ Removed from sync queue:', syncId)
  } catch (error) {
    console.error('❌ Failed to remove from sync queue:', error)
  }
}

const updateSyncRetryCount = async (syncId: number, retryCount: number) => {
  try {
    const db = await openIndexedDB()
    const transaction = db.transaction(['syncQueue'], 'readwrite')
    const store = transaction.objectStore('syncQueue')
    
    const syncItem = await new Promise<SyncItem | null>((resolve) => {
      const request = store.get(syncId)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => resolve(null)
    })
    
    if (syncItem) {
      syncItem.retry_count = retryCount
      await store.put(syncItem)
    }
    db.close()
  } catch (error) {
    console.error('❌ Failed to update sync retry count:', error)
  }
}

// Sync user data when online
const syncUserData = async () => {
  if (!navigator.onLine) return
  
  try {
    const syncItems = await getSyncQueue()
    
    for (const item of syncItems) {
      try {
        if (item.type === 'user_update') {
          // Sync user data to server
          const response = await fetch(`${API_BASE_URL}/api/users/${item.user_id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(item.data)
          })
          
          if (response.ok) {
            await removeFromSyncQueue(item.id!)
            console.log('✅ Synced user data for:', item.user_id)
          } else {
            // Increment retry count
            await updateSyncRetryCount(item.id!, item.retry_count + 1)
            if (item.retry_count >= 3) {
              await removeFromSyncQueue(item.id!)
              console.warn('🗑️ Removed failed sync item after 3 retries:', item.id)
            }
          }
        } else if (item.type === 'activity_record') {
          // Sync activity data to server
          const response = await fetch(`${API_BASE_URL}/api/users/${item.user_id}/activity`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(item.data)
          })
          
          if (response.ok) {
            await removeFromSyncQueue(item.id!)
            console.log('✅ Synced activity data for:', item.user_id)
          } else {
            await updateSyncRetryCount(item.id!, item.retry_count + 1)
            if (item.retry_count >= 3) {
              await removeFromSyncQueue(item.id!)
              console.warn('🗑️ Removed failed sync item after 3 retries:', item.id)
            }
          }
        }
      } catch (error) {
        console.error('❌ Failed to sync item:', item.id, error)
        await updateSyncRetryCount(item.id!, item.retry_count + 1)
        if (item.retry_count >= 3) {
          await removeFromSyncQueue(item.id!)
          console.warn('🗑️ Removed failed sync item after 3 retries:', item.id)
        }
      }
    }
  } catch (error) {
    console.error('❌ Failed to sync user data:', error)
  }
}

const parseTranscript = (transcriptData: Earworm['transcript'] | null | undefined) => {
  try {
    if (!transcriptData) {
      return []
    }

    const payload = typeof transcriptData === 'string' ? JSON.parse(transcriptData) : transcriptData
    const aligned = payload?.alignedWords
    if (!Array.isArray(aligned)) {
      return []
    }

    return aligned
      .filter((item: any) => item?.word && item.word.trim())
      .map((item: any) => {
        const startValue =
          typeof item.start === 'number'
            ? item.start
            : typeof item.startS === 'number'
              ? item.startS
              : undefined
        const endValue =
          typeof item.end === 'number'
            ? item.end
            : typeof item.endS === 'number'
              ? item.endS
              : undefined

        return {
          word: item.word,
          start: startValue,
          end: endValue
        }
      })
  } catch (error) {
    console.error('Failed to parse transcript:', error)
    return []
  }
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString()
}
</script>

<style scoped>
.earworms-container {
  min-height: 100vh;
  background: #f8f9fa;
}

.page-main {
  padding: 1rem;
  max-width: 1200px;
  margin: 0 auto;
}

.action-btn-bar {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 2rem;
}

.action-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.construct-btn {
  background: #43a047;
  color: white;
}

.construct-btn:hover {
  background: #388e3c;
}

.loading {
  text-align: center;
  padding: 2rem;
  font-size: 1.1rem;
  color: #666;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #666;
}

.empty-state p {
  font-size: 1.2rem;
  margin-bottom: 1rem;
}

.tiles-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.earworm-tile {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.earworm-tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}

.earworm-header {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.earworm-image {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.earworm-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.placeholder-image {
  width: 100%;
  height: 100%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
}

.earworm-info {
  flex: 1;
}

.earworm-info h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
  color: #333;
}

.earworm-stats {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.stat-item {
  color: #666;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.source {
  margin: 0 0 0.5rem 0;
  color: #666;
  font-size: 0.9rem;
}

.earworm-actions {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.audio-controls {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.play-btn, .pause-btn, .stop-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.play-btn {
  background: #2196f3;
  color: white;
}

.play-btn:hover {
  background: #1976d2;
}

.download-btn {
  background: #4caf50;
  color: white;
}

.download-btn:hover {
  background: #388e3c;
}

.download-btn:disabled {
  background: #cccccc;
  cursor: not-allowed;
}

.music-status {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  text-transform: capitalize;
}

.music-status.generating {
  background: rgba(255, 152, 0, 0.1);
  color: #ff9800;
  border: 1px solid rgba(255, 152, 0, 0.3);
}

.music-status.no-audio {
  background: rgba(156, 156, 156, 0.1);
  color: #999;
  border: 1px solid rgba(156, 156, 156, 0.3);
}

.earworm-meta {
  display: flex;
  justify-content: space-between;
  color: #999;
  font-size: 0.8rem;
}
</style>